import React, { useRef, useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import imageCompression from 'browser-image-compression';
import { jsPDF } from 'jspdf';
import { PDFDocument } from 'pdf-lib';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';
import pdfjsWorker from 'pdfjs-dist/legacy/build/pdf.worker.entry';
import {
  useDataEntry,
  useQuoteDocument,
  useDraftBlDocument,
  useCopyDocuments,
  useContainerImage,
} from '../../queries/dataEntryQueries';
import {
  useUploadQuoteDocument,
  useUpdateQuoteDocument,
  useDeleteQuoteDocument,
  useUploadDraftBlDocument,
  useUpdateDraftBlDocument,
  useDeleteDraftBlDocument,
  useUploadCopyDocument,
  useDeleteCopyDocument,
  useUploadContainerImage,
  useDeleteContainerImage,
} from '../../mutations/dataEntryMutations';

/**
 * DocumentUploadView — Document Upload page
 *
 * ✅ Calls all GET document APIs on page load to check for existing files.
 * ✅ If a document already exists → shows filename + Replace / Delete buttons.
 * ✅ If no document → shows Upload button.
 * ✅ POST on first upload, PUT on replace.
 */

// ── PDF.js Worker Setup ─────────────────────────────────────────────────────────
if (typeof window !== 'undefined' && pdfjsLib.GlobalWorkerOptions) {
  pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;
}

// ── Constants ─────────────────────────────────────────────────────────────────

/** Maximum allowed file size in bytes (2.5 MB) */
const MAX_FILE_SIZE_BYTES = 2.5 * 1024 * 1024;

/** Allowed image MIME types */
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

/** Allowed image accept string for <input> */
const ALLOWED_IMAGE_ACCEPT = '.jpg,.jpeg,.png,.webp';

/** Target compressed size in MB for images (~500 KB) */
const IMAGE_COMPRESS_TARGET_MB = 0.49;

// ── Validation Helpers ─────────────────────────────────────────────────────────

/**
 * Validates a PDF file before compression.
 * Returns null if valid, or an error string if invalid.
 */
const validatePdfFile = (file) => {
  if (!file) return 'No file selected.';
  if (file.type !== 'application/pdf') return 'Only PDF files are allowed.';
  if (file.size > MAX_FILE_SIZE_BYTES) return 'File size exceeds 2.5 MB limit. Please upload a file of 2.5 MB and below.';
  return null;
};

/**
 * Validates an image file type.
 * Returns null if valid type, or an error string if invalid type.
 */
const validateImageType = (file) => {
  if (!file) return 'No file selected.';
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) return 'Only JPG, PNG and WEBP images are allowed.';
  return null;
};

// ── Image Compression Helper ───────────────────────────────────────────────────

/**
 * Validates and compresses an image file.
 * Steps:
 *   1. Validate file type.
 *   2. Reject immediately if > 10 MB.
 *   3. If within limit but > 500 KB, compress to ~500 KB.
 *   4. Return { file, error, warning }
 */
const processImageFile = async (file, onCompressing) => {
  if (!file) return { file: null, error: 'No file selected.', warning: null };

  // 1. Type check
  const typeError = validateImageType(file);
  if (typeError) return { file: null, error: typeError, warning: null };

  // 2. Hard size check
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return { file: null, error: 'File size must be less than 10 MB.', warning: null };
  }

  // 3. Below or equal to 500 KB — skip compression
  const targetBytes = 500 * 1024;
  if (file.size <= targetBytes) {
    return { file, error: null, warning: null };
  }

  // 4. Compress images > 500 KB
  if (typeof onCompressing === 'function') onCompressing(true);
  try {
    const options = {
      maxSizeMB: IMAGE_COMPRESS_TARGET_MB,
      maxWidthOrHeight: 1920,
      useWebWorker: true,
      initialQuality: 0.85,
    };
    const compressedBlob = await imageCompression(file, options);
    const compressedFile = new File([compressedBlob], file.name, {
      type: compressedBlob.type || file.type,
    });
    if (typeof onCompressing === 'function') onCompressing(false);
    return { file: compressedFile, error: null, warning: null };
  } catch {
    if (typeof onCompressing === 'function') onCompressing(false);
    return {
      file: null,
      error: `Image "${file.name}" could not be optimized. Please try a different file.`,
      warning: null,
    };
  }
};

// ── PDF Compression Helper ─────────────────────────────────────────────────────

/**
 * Two-stage PDF compression using only the project's existing libraries.
 *
 * STAGE 1 — Lossless (pdf-lib):
 *   Strips unused objects, deduplicates font/resource streams, compresses
 *   cross-reference tables. Fast (~50 ms). No visual change.
 *   → If result ≤ 2.5 MB, done.
 *
 * STAGE 2 — High-quality rasterisation (pdfjs-dist + jspdf):
 *   Each page is rendered to canvas at 1.5× DPI (150 DPI equivalent for a
 *   typical 100 DPI PDF), then encoded as JPEG at 85% quality.
 *   85% JPEG is visually identical to the original for scanned trade docs;
 *   it typically saves 50–80% of file size.
 *
 * Files ≤ 2.5 MB are passed through unchanged.
 * The result is always the smallest candidate that is smaller than the original.
 */
const PDF_SKIP_BYTES = 2.5 * 1024 * 1024; // 2.5 MB — skip if already within limit

/** Render each PDF page to a canvas and re-pack as a JPEG-based PDF. */
const rasterisePdf = async (arrayBuffer, renderScale, jpegQuality) => {
  const loadingTask = pdfjsLib.getDocument({
    data: new Uint8Array(arrayBuffer),
    verbosity: 0,
  });
  const pdfDoc = await loadingTask.promise;
  const numPages = pdfDoc.numPages;

  const { w: fw, h: fh } = await (async () => {
    const firstPage = await pdfDoc.getPage(1);
    const vp = firstPage.getViewport({ scale: renderScale });
    return { w: vp.width, h: vp.height };
  })();

  const pxToMm = 25.4 / 96; // screen px → mm at 96 DPI
  const pdf = new jsPDF({
    orientation: fw >= fh ? 'l' : 'p',
    unit: 'mm',
    format: [fw * pxToMm, fh * pxToMm],
    compress: true,
  });

  for (let pageNum = 1; pageNum <= numPages; pageNum++) {
    const page = await pdfDoc.getPage(pageNum);
    const viewport = page.getViewport({ scale: renderScale });

    const canvas = document.createElement('canvas');
    canvas.width  = Math.round(viewport.width);
    canvas.height = Math.round(viewport.height);
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    await page.render({ canvasContext: ctx, viewport }).promise;

    const { w, h } = { w: viewport.width, h: viewport.height };
    const imgData = canvas.toDataURL('image/jpeg', jpegQuality);

    if (pageNum > 1) {
      pdf.addPage([w * pxToMm, h * pxToMm], w >= h ? 'l' : 'p');
    }
    pdf.addImage(imgData, 'JPEG', 0, 0, w * pxToMm, h * pxToMm, `p${pageNum}`, 'FAST');

    console.log(`[compressPdfFile] Rendered page ${pageNum}/${numPages}`);
  }

  return pdf.output('blob');
};

const compressPdfFile = async (file, onCompressing) => {
  if (!file) return file;

  console.log(`[compressPdfFile] "${file.name}" — ${(file.size / 1024 / 1024).toFixed(2)} MB`);

  if (file.size <= PDF_SKIP_BYTES) {
    console.log('[compressPdfFile] ≤ 2.5 MB — skipping.');
    return file;
  }

  console.log('[compressPdfFile] > 2.5 MB — starting compression...');
  if (typeof onCompressing === 'function') onCompressing(true);

  try {
    const arrayBuffer = await file.arrayBuffer();
    let bestFile = null;

    // ── STAGE 1: Lossless pdf-lib structural compression ──────────────────────
    try {
      const pdfLibDoc = await PDFDocument.load(arrayBuffer, {
        ignoreEncryption: true,
        updateMetadata: false,
      });
      const pdfLibBytes = await pdfLibDoc.save({ useObjectStreams: true });
      const pdfLibBlob = new Blob([pdfLibBytes], { type: 'application/pdf' });
      console.log(`[compressPdfFile] Stage 1 (lossless): ${(pdfLibBlob.size / 1024).toFixed(0)} KB`);

      if (pdfLibBlob.size < file.size) {
        bestFile = new File([pdfLibBlob], file.name, {
          type: 'application/pdf',
          lastModified: file.lastModified,
        });
        // If lossless pass already reached the target, we're done.
        if (pdfLibBlob.size <= PDF_SKIP_BYTES) {
          const saved = (((file.size - bestFile.size) / file.size) * 100).toFixed(1);
          console.log(`[compressPdfFile] SUCCESS (lossless)! ${(file.size/1024).toFixed(0)} KB → ${(bestFile.size/1024).toFixed(0)} KB (−${saved}%)`);
          if (typeof onCompressing === 'function') onCompressing(false);
          return bestFile;
        }
      }
    } catch (pdfLibErr) {
      console.warn('[compressPdfFile] Stage 1 skipped:', pdfLibErr?.message);
    }

    // ── STAGE 2: High-quality rasterisation ──────────────────────────────────
    // Render at 1.5× scale, encode as JPEG at 85% quality.
    // Result looks identical to the original for trade documents.
    console.log('[compressPdfFile] Stage 2: rasterising at 1.5× / JPEG 85%...');
    const rasterBlob = await rasterisePdf(arrayBuffer, 1.5, 0.85);
    console.log(`[compressPdfFile] Stage 2 result: ${(rasterBlob.size / 1024).toFixed(0)} KB`);

    // Pick the smallest result that is actually smaller than the original.
    const candidates = [
      bestFile,
      rasterBlob.size < file.size
        ? new File([rasterBlob], file.name, { type: 'application/pdf', lastModified: file.lastModified })
        : null,
    ].filter(Boolean);

    if (candidates.length === 0) {
      console.warn('[compressPdfFile] No candidate smaller than original. Returning original.');
      if (typeof onCompressing === 'function') onCompressing(false);
      return file;
    }

    const result = candidates.reduce((a, b) => (a.size <= b.size ? a : b));
    const saved = (((file.size - result.size) / file.size) * 100).toFixed(1);
    console.log(`[compressPdfFile] SUCCESS! ${(file.size/1024).toFixed(0)} KB → ${(result.size/1024).toFixed(0)} KB (−${saved}%)`);
    if (typeof onCompressing === 'function') onCompressing(false);
    return result;
  } catch (err) {
    console.error('[compressPdfFile] ERROR:', err);
    if (typeof onCompressing === 'function') onCompressing(false);
    return file;
  }
};

// ── Validation / Error Banner ──────────────────────────────────────────────────

/**
 * Inline error or warning banner.
 * type = 'error'   → red   alert-danger
 * type = 'warning' → yellow alert-warning
 */
const FileValidationBanner = ({ message, type = 'error' }) => {
  if (!message) return null;
  const alertClass = type === 'warning' ? 'alert-warning' : 'alert-danger';
  const icon = type === 'warning' ? '⚠️' : '❌';
  return (
    <div
      className={`alert ${alertClass} d-flex align-items-start gap-2 py-2 px-3 mb-2 small`}
      role="alert"
    >
      <span style={{ fontSize: '1rem' }}>{icon}</span>
      <span>{message}</span>
    </div>
  );
};

// Keep old name as alias so nothing else in the codebase breaks
const FileSizeWarning = ({ message }) => <FileValidationBanner message={message} type="warning" />;

// ── Format bytes ──────────────────────────────────────────────────────────────

const formatBytes = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

// ── Container list extractor ───────────────────────────────────────────────────

const extractContainerList = (source) => {
  if (!source) return [];
  if (Array.isArray(source)) {
    return source
      .map((c) => (typeof c === 'object' ? c.containerNo || c.container_no : c))
      .filter(Boolean);
  }
  if (typeof source === 'string') {
    return source.split(',').map((s) => s.trim()).filter(Boolean);
  }
  return [];
};

// ── Single Document Card ────────────────────────────────────────────────────────
// Used for Draft BL and Quote (Proforma) documents.
// Calls GET on mount → if file exists show with Replace/Delete; else show Upload.

const SingleDocCard = ({
  title,
  icon,
  fileName,
  blNo,
  useGetQuery,
  uploadMutation,
  updateMutation,
  deleteMutation,
}) => {
  const inputRef = useRef(null);
  const [validationError, setValidationError] = useState(null);
  // pendingFile: tracks the file being uploaded for optimistic UI during upload
  const [pendingFile, setPendingFile] = useState(null);
  // isCompressing: true while pdf-lib is re-serializing the PDF
  const [isCompressing, setIsCompressing] = useState(false);

  /**
   * GET API — server-authoritative document state.
   * staleTime:0 means invalidateQueries (called by upload/delete mutations)
   * triggers an immediate background refetch. The UI is then always derived
   * from fresh server data — no local state that can get stuck.
   */
  const { data: existingDoc, isLoading: isLoadingDoc } = useGetQuery(fileName, blNo);

  // Derive display state directly from the query — no local useState for this.
  // Every render reflects the actual server state (or the in-progress upload).
  const serverFileName = existingDoc?.fileName || existingDoc?.name || null;
  // While an upload is in progress, show the pending file name optimistically.
  const displayedFileName = pendingFile?.name ?? serverFileName;
  const hasDocument = !!serverFileName; // use server truth for PUT vs POST decision

  const isPending =
    uploadMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  const isBusy = isPending || isCompressing;

  const handleUpload = async (e) => {
    const file = e.target?.files?.[0];
    if (!file) return;
    setValidationError(null);
    setPendingFile(null);

    // ── PDF-only validation (type + 2.5 MB size) ──
    const pdfError = validatePdfFile(file);
    if (pdfError) {
      setValidationError(pdfError);
      if (e.target) e.target.value = null;
      return;
    }

    // Prevent duplicate upload while already processing
    if (isBusy) return;

    // ── Lossless PDF compression ──
    // pdf-lib re-serializes using object streams (deflate). Text/fonts untouched.
    const compressedFile = await compressPdfFile(file, setIsCompressing);

    setPendingFile({ name: compressedFile.name, size: compressedFile.size });

    const formData = new FormData();
    formData.append('file', compressedFile);

    const mutation = hasDocument ? updateMutation : uploadMutation;
    mutation.mutate(
      { fileName, blNo, data: formData },
      {
        // On success: clear pendingFile; mutation-level invalidateQueries + refetch
        // will update existingDoc → serverFileName → UI automatically.
        onSuccess: () => setPendingFile(null),
        onError: (err) => {
          setPendingFile(null);
          if (err?.response?.status === 413) {
            setValidationError('File size exceeds 2.5 MB limit. Please upload a file of 2.5 MB and below.');
          }
        },
      }
    );
    e.target.value = null;
  };

  /**
   * handleDelete — no local state update needed.
   * Mutation-level onSuccess calls invalidateQueries → query refetches →
   * existingDoc becomes null → serverFileName = null → hasDocument = false →
   * component renders the Upload button automatically.
   *
   * Safe fallback: 404 means the document was already deleted on the server;
   * the invalidateQueries from the mutation-level onError still fires and
   * the query refetch will clear existingDoc. No manual state reset needed.
   */
  const handleDelete = () => {
    deleteMutation.mutate({ fileName, blNo });
  };

  return (
    <div className="card border-0 shadow-sm mb-3">
      {/* Header */}
      <div className="card-header bg-white d-flex align-items-center gap-2 py-2 border-bottom">
        <span>{icon}</span>
        <span
          className="fw-semibold small text-uppercase"
          style={{ letterSpacing: '0.5px', color: 'var(--gtss-primary)' }}
        >
          {title}
        </span>
      </div>

      {/* Body */}
      <div className="card-body py-3">
        {/* Validation error */}
        <FileValidationBanner message={validationError} type="error" />
        <input
          type="file"
          ref={inputRef}
          accept="application/pdf,.pdf"
          className="d-none"
          onChange={handleUpload}
        />

        {/* While initial GET is in-flight (no cached data yet) */}
        {isLoadingDoc && !displayedFileName ? (
          <div className="d-flex align-items-center gap-2 text-muted small">
            <span className="spinner-border spinner-border-sm" role="status" />
            Checking for existing document...
          </div>
        ) : displayedFileName ? (
          /* File exists (or uploading) — show filename + Replace + Delete */
          <div className="d-flex align-items-center justify-content-between gap-2 bg-light rounded px-3 py-2">
            <span className="small text-truncate flex-grow-1">
              📄 {displayedFileName}
              {isCompressing ? (
                <span className="badge bg-info text-dark ms-2 align-middle">
                  <span
                    className="spinner-border spinner-border-sm me-1"
                    role="status"
                    style={{ width: '0.7rem', height: '0.7rem', borderWidth: '0.12em' }}
                  />
                  Compressing...
                </span>
              ) : uploadMutation.isPending || updateMutation.isPending || pendingFile ? (
                <span className="badge bg-warning text-dark ms-2 align-middle">
                  <span
                    className="spinner-border spinner-border-sm me-1"
                    role="status"
                    style={{ width: '0.7rem', height: '0.7rem', borderWidth: '0.12em' }}
                  />
                  Uploading...{pendingFile ? ` (${formatBytes(pendingFile.size)})` : ''}
                </span>
              ) : (
                <span className="badge bg-success ms-2">Uploaded</span>
              )}
            </span>
            <div className="d-flex gap-2 flex-shrink-0">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => inputRef.current?.click()}
                disabled={isBusy}
              >
                {isCompressing ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1" role="status" />
                    Compressing...
                  </>
                ) : uploadMutation.isPending || updateMutation.isPending ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1" role="status" />
                    Uploading...
                  </>
                ) : (
                  '🔄 Replace'
                )}
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                onClick={handleDelete}
                disabled={isBusy || !!pendingFile}
                title={pendingFile ? 'Upload in progress...' : 'Delete document'}
              >
                {deleteMutation.isPending ? (
                  <span className="spinner-border spinner-border-sm" />
                ) : (
                  '🗑'
                )}
              </button>
            </div>
          </div>
        ) : (
          /* No file — show Upload button + hint */
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <button
              type="button"
              className="btn btn-sm btn-orange"
              onClick={() => inputRef.current?.click()}
              disabled={isBusy}
            >
              {isCompressing ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1" role="status" />
                  Compressing...
                </>
              ) : isPending ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1" role="status" />
                  Uploading...
                </>
              ) : (
                '📎 Upload'
              )}
            </button>
            <span className="text-muted small">No document uploaded.</span>
            <span className="text-muted small" style={{ fontSize: '0.75rem' }}>
              PDF only · max 2.5 MB
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Copy Documents Card ────────────────────────────────────────────────────────
// Calls GET /data-entry/copy-documents?fileName=&blNo= on load.
// Merges server files with session-uploaded files in a single list.

const CopyDocsCard = ({ fileName, blNo }) => {
  const inputRef = useRef(null);
  const [validationError, setValidationError] = useState(null);
  // isCompressing: true while pdf-lib is processing the selected files
  const [isCompressing, setIsCompressing] = useState(false);

  /**
   * GET API — always read the server-authoritative list.
   * staleTime:0 on this query means invalidateQueries (fired by the mutation)
   * triggers an immediate background refetch, so the list always reflects
   * the real server state — every item has a proper server ID.
   *
   * This eliminates the race condition where an optimistic item had id:null
   * and the delete handler silently skipped the API call.
   */
  const { data: existingDocs, isLoading: isLoadingDocs } = useCopyDocuments(fileName, blNo);

  // Derive displayed list DIRECTLY from the query — never from local state.
  // Every item in docList has a real server ID because it came from the GET API.
  const docList = useMemo(() => {
    if (!Array.isArray(existingDocs)) return [];
    return existingDocs.map((doc) => ({
      id: doc.id,
      fileName: doc.fileName || doc.name || 'document',
    }));
  }, [existingDocs]);

  const uploadMutation = useUploadCopyDocument();
  const deleteMutation = useDeleteCopyDocument();

  const isBusy = uploadMutation.isPending || isCompressing;

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setValidationError(null);

    // ── PDF-only validation (type + 2.5 MB size) ──
    for (const file of files) {
      const pdfError = validatePdfFile(file);
      if (pdfError) {
        setValidationError(files.length > 1 ? `"${file.name}": ${pdfError}` : pdfError);
        e.target.value = null;
        return;
      }
    }

    // Prevent duplicate upload while already processing
    if (isBusy) return;

    // ── Lossless PDF compression for each file ──
    // Process all files in parallel using pdf-lib object stream compression.
    // Falls back to the original file on error (never blocks the upload).
    setIsCompressing(true);
    const compressedFiles = await Promise.all(
      files.map((file) => compressPdfFile(file))
    );
    setIsCompressing(false);

    // Batch all compressed files into ONE FormData → single API call
    const formData = new FormData();
    compressedFiles.forEach((file) => {
      formData.append('files', file);
      formData.append('file', file);
    });

    // No onSuccess state update needed — mutation-level invalidateQueries
    // causes the query to refetch, and docList updates automatically from server.
    uploadMutation.mutate(
      { fileName, blNo, data: formData },
      {
        onError: (err) => {
          if (err?.response?.status === 413) {
            setValidationError('File size exceeds 2.5 MB limit. Please upload a file of 2.5 MB and below.');
          }
        },
      }
    );
    e.target.value = null;
  };

  /**
   * Delete always has a real server ID because docList comes from the GET query.
   * If id is somehow missing (extreme edge case), log and skip — do NOT silently
   * remove from UI, as the file still exists on the server.
   */
  const handleDelete = (doc) => {
    if (!doc?.id) {
      console.warn('[CopyDocsCard] Delete skipped — item has no server ID:', doc);
      return;
    }
    deleteMutation.mutate(doc.id);
    // No manual state update — mutation-level invalidateQueries + query refetch
    // will automatically remove the item from docList.
  };

  const resolvedList = docList;

  return (
    <div className="card border-0 shadow-sm mb-3">
      {/* Header */}
      <div className="card-header bg-white d-flex align-items-center justify-content-between py-2 border-bottom">
        <div className="d-flex align-items-center gap-2">
          <span>📋</span>
          <span
            className="fw-semibold small text-uppercase"
            style={{ letterSpacing: '0.5px', color: 'var(--gtss-primary)' }}
          >
            Copy Documents
          </span>
          {resolvedList.length > 0 && (
            <span className="badge bg-secondary">{resolvedList.length}</span>
          )}
        </div>
        <div>
          <input
            type="file"
            ref={inputRef}
            accept="application/pdf,.pdf"
            className="d-none"
            multiple
            onChange={handleUpload}
          />
          <button
            type="button"
            className="btn btn-sm btn-orange"
            onClick={() => inputRef.current?.click()}
            disabled={isBusy}
          >
            {isCompressing ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" role="status" />
                Compressing...
              </>
            ) : uploadMutation.isPending ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" role="status" />
                Uploading...
              </>
            ) : (
              '📎 Add Files'
            )}
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="card-body py-3">
        {/* Validation error */}
        <FileValidationBanner message={validationError} type="error" />
        {isLoadingDocs && docList === undefined ? (
          <div className="d-flex align-items-center gap-2 text-muted small">
            <span className="spinner-border spinner-border-sm" role="status" />
            Checking for existing documents...
          </div>
        ) : resolvedList.length === 0 ? (
          <p className="text-muted small mb-0">No files uploaded.</p>
        ) : (
          <ul className="list-group list-group-flush">
            {resolvedList.map((doc, idx) => (
              <li
                key={doc.id ?? idx}
                className="list-group-item d-flex align-items-center justify-content-between px-0 py-2 border-0"
              >
                <span className="small text-truncate flex-grow-1">
                  📄 {doc.fileName}
                  <span className="badge bg-success ms-2">Uploaded</span>
                </span>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger ms-2 flex-shrink-0"
                  onClick={() => handleDelete(doc)}
                  disabled={deleteMutation.isPending || !doc.id}
                  title={!doc.id ? 'Saving to server...' : 'Delete document'}
                >
                  {deleteMutation.isPending ? (
                    <span className="spinner-border spinner-border-sm" />
                  ) : (
                    '🗑'
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

// ── Container Row — per-container image state ──────────────────────────────────
// Each row calls its own GET API — GET /data-entry/container-image?fileName=&blNo=&containerNo=

const ContainerRow = ({ idx, fileName, blNo, containerNo }) => {
  const fileInputRef = useRef(null);

  // GET API — fetch existing images for this container
  const { data: existingImages, isLoading: isLoadingImages } = useContainerImage(
    fileName,
    blNo,
    containerNo
  );

  // Local state: array of { id, fileName } | undefined = not yet initialized
  const [imageList, setImageList] = useState(undefined);

  // Sync GET result → local state
  useEffect(() => {
    if (isLoadingImages) return;
    if (!existingImages) {
      if (imageList === undefined) setImageList([]);
      return;
    }

    let images = [];
    if (Array.isArray(existingImages)) {
      images = existingImages;
    } else if (Array.isArray(existingImages?.containerImage)) {
      images = existingImages.containerImage;
    } else if (Array.isArray(existingImages?.files)) {
      images = existingImages.files;
    } else if (Array.isArray(existingImages?.images)) {
      images = existingImages.images;
    } else if (Array.isArray(existingImages?.data)) {
      images = existingImages.data;
    } else if (typeof existingImages === 'object' && existingImages !== null) {
      images = [existingImages];
    } else if (typeof existingImages === 'string') {
      images = [existingImages];
    }

    const items = images
      .map((img, i) => {
        if (!img) return null;
        if (typeof img === 'object') {
          return {
            id: img.id,
            fileName:
              img.fileName ||
              img.name ||
              img.originalName ||
              img.filename ||
              `Image ${i + 1}`,
          };
        }
        if (typeof img === 'string') {
          return {
            id: null,
            fileName: img.startsWith('data:') || img.length > 100 ? `Image ${i + 1}` : img,
          };
        }
        return { id: null, fileName: `Image ${i + 1}` };
      })
      .filter(Boolean);

    setImageList(items);
  }, [existingImages, isLoadingImages]);

  const uploadMutation = useUploadContainerImage();
  const deleteMutation = useDeleteContainerImage();

  const [validationError, setValidationError] = useState(null);
  // true while browser-image-compression is running
  const [isCompressing, setIsCompressing] = useState(false);

  const handleFileChange = async (e) => {
    const files = Array.from(e.target?.files || []);
    if (!files.length) return;
    setValidationError(null);

    // Prevent duplicate upload while already uploading or compressing
    if (uploadMutation.isPending || isCompressing) return;

    // ── Step 1: Validate types and sizes immediately ──
    for (const file of files) {
      const typeError = validateImageType(file);
      if (typeError) {
        setValidationError(typeError);
        e.target.value = null;
        return;
      }
      if (file.size > MAX_FILE_SIZE_BYTES) {
        setValidationError('File size must be less than 2.5 MB.');
        e.target.value = null;
        return;
      }
    }

    // ── Step 2: Compress each image — show optimizing state ──
    const results = await Promise.all(
      files.map((file) => processImageFile(file, setIsCompressing))
    );

    // Collect hard errors
    const errors = results.map((r) => r.error).filter(Boolean);
    if (errors.length > 0) {
      setValidationError(errors.join(' | '));
      e.target.value = null;
      return;
    }

    // Only upload files that passed
    const validFiles = results.map((r) => r.file).filter(Boolean);
    if (!validFiles.length) {
      e.target.value = null;
      return;
    }

    const formData = new FormData();
    validFiles.forEach((file) => formData.append('files', file));

    // No optimistic state update — rely on the query refetch (triggered by
    // mutation-level invalidateQueries) to populate imageList with proper server IDs.
    // This prevents the race condition where id:null items bypass the delete API.
    uploadMutation.mutate(
      { fileName, blNo, containerNo, data: formData },
      {
        onError: (err) => {
          if (err?.response?.status === 413) {
            setValidationError('File size exceeds 2.5 MB limit. Please upload a file of 2.5 MB and below.');
          }
        },
      }
    );
    e.target.value = null;
  };

  /**
   * handleDeleteItem — Safe delete with server-ID guard.
   *
   * Items in imageList that have id:null are freshly uploaded items still waiting
   * for the query refetch to confirm their server ID. We disable the X button for
   * these items in the UI, but also guard here as a safety fallback.
   */
  const handleDeleteItem = (item, idx) => {
    if (!item?.id) {
      // Item not yet confirmed by server — skip. The X button should already
      // be hidden/disabled for these items, but this is a safe fallback.
      console.warn('[ContainerRow] Delete skipped — image has no server ID yet:', item);
      return;
    }
    deleteMutation.mutate(item.id, {
      onSuccess: () => {
        setImageList((prev) => (prev || []).filter((_, i) => i !== idx));
      },
    });
  };

  const handleDeleteAll = () => {
    const itemsWithId = (imageList || []).filter((item) => item?.id);
    const pendingItems = (imageList || []).filter((item) => !item?.id);

    // Only delete items confirmed by the server
    itemsWithId.forEach((item) => deleteMutation.mutate(item.id));

    // Remove confirmed items from UI; keep pending items (they'll be overwritten
    // by the query refetch once the server confirms them)
    setImageList(pendingItems.length > 0 ? pendingItems : []);
  };

  const resolvedList = imageList || [];
  const isBusy = uploadMutation.isPending || isCompressing;

  return (
    <tr>
      <td className="text-center text-muted fw-bold">{idx + 1}</td>
      <td>
        <span className="fw-semibold text-dark">🚢 {containerNo}</span>
      </td>
      <td>
        {/* Optimizing / validation state — shown inside the images cell */}
        {isCompressing && (
          <div className="d-flex align-items-center gap-2 text-muted small mb-1">
            <span className="spinner-border spinner-border-sm" role="status" />
            Optimizing image...
          </div>
        )}
        {validationError && !isCompressing && (
          <FileValidationBanner message={validationError} type="error" />
        )}
        {isLoadingImages && imageList === undefined ? (
          <span className="text-muted fst-italic small">
            <span className="spinner-border spinner-border-sm me-1" role="status" />
            Checking...
          </span>
        ) : resolvedList.length > 0 ? (
          <div className="d-flex flex-wrap gap-1 align-items-center">
            {resolvedList.map((item, i) => (
              <span
                key={item.id ?? i}
                className="badge bg-success-subtle text-success border border-success-subtle px-2 py-1 d-inline-flex align-items-center gap-1"
              >
                📷 {item.fileName}
                {item.id ? (
                  /* Item confirmed by server — show delete X */
                  <button
                    type="button"
                    className="btn-close ms-1"
                    style={{ fontSize: '0.55rem' }}
                    onClick={() => handleDeleteItem(item, i)}
                    title="Delete this image"
                  />
                ) : (
                  /* Item not yet confirmed — show saving spinner, no delete */
                  <span
                    className="spinner-border ms-1"
                    style={{ width: '0.55rem', height: '0.55rem', borderWidth: '0.1em' }}
                    role="status"
                    title="Saving to server..."
                  />
                )}
              </span>
            ))}
          </div>
        ) : (
          <span className="text-muted fst-italic">No images uploaded</span>
        )}
      </td>
      <td className="text-end pe-3">
        <input
          type="file"
          ref={fileInputRef}
          accept={ALLOWED_IMAGE_ACCEPT}
          multiple
          className="d-none"
          onChange={handleFileChange}
        />
        <div className="d-inline-flex gap-1">
          <button
            type="button"
            className="btn btn-sm btn-orange"
            disabled={isBusy}
            onClick={() => fileInputRef.current?.click()}
          >
            {isCompressing ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" role="status" />
                Optimizing...
              </>
            ) : uploadMutation.isPending ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" role="status" />
                Uploading...
              </>
            ) : (
              '📷 Upload Images'
            )}
          </button>
          {resolvedList.length > 0 && (
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              disabled={deleteMutation.isPending}
              onClick={handleDeleteAll}
              title="Delete all images for this container"
            >
              {deleteMutation.isPending ? (
                <span className="spinner-border spinner-border-sm" />
              ) : (
                '🗑'
              )}
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};

// ── Container Images Card ──────────────────────────────────────────────────────
// Renders one ContainerRow per container — each row calls GET ?fileName=&blNo=&containerNo=

const ContainerImagesCard = ({ fileName, blNo, containerList }) => {
  return (
    <div className="card border-0 shadow-sm mb-3">
      <div className="card-header bg-white d-flex align-items-center gap-2 py-2 border-bottom">
        <span>🚢</span>
        <span
          className="fw-semibold small text-uppercase"
          style={{ letterSpacing: '0.5px', color: 'var(--gtss-primary)' }}
        >
          Container Images
        </span>
        {containerList.length > 0 && (
          <span className="badge bg-secondary">
            {containerList.length} container{containerList.length > 1 ? 's' : ''}
          </span>
        )}
      </div>
      <div className="card-body py-3">
        {containerList.length === 0 ? (
          <p className="text-muted small mb-0">No containers associated with this record.</p>
        ) : (
          <div className="table-responsive rounded border mb-0">
            <table className="table table-hover align-middle mb-0 small">
              <thead className="table-light text-uppercase">
                <tr>
                  <th style={{ width: '50px' }} className="text-center">#</th>
                  <th>Container Number</th>
                  <th>Uploaded Images</th>
                  <th style={{ width: '220px' }} className="text-end pe-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {containerList.map((containerNo, idx) => (
                  <ContainerRow
                    key={containerNo}
                    idx={idx}
                    fileName={fileName}
                    blNo={blNo}
                    containerNo={containerNo}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Main DocumentUploadView ────────────────────────────────────────────────────

const DocumentUploadView = ({ dataEntryId }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Context from router state (passed by DataManagement navigate)
  const stateFileName = location.state?.fileName;
  const stateBlNo = location.state?.blNo;
  const stateContainerList =
    location.state?.containerList ||
    extractContainerList(
      location.state?.containerNo ||
      location.state?.containersNumber ||
      location.state?.containerNos
    );

  // Fallback: GET /data-entry/{id} if router state is missing any field
  const { data: entryData } = useDataEntry(dataEntryId);

  const fileName = stateFileName || entryData?.fileName || '';
  const blNo = stateBlNo || entryData?.blNo || entryData?.billno || '';

  // Build full, deduplicated container list
  const fetchedContainerList = extractContainerList(entryData?.userForm)
    .concat(extractContainerList(entryData?.dataEntryForm))
    .concat(extractContainerList(entryData?.containers));

  const rawContainerList =
    stateContainerList && stateContainerList.length > 0
      ? stateContainerList
      : fetchedContainerList.length > 0
        ? fetchedContainerList
        : extractContainerList(
          entryData?.containerNo || entryData?.containersNumber || entryData?.containerNos
        );

  const containerList = Array.from(new Set(rawContainerList.filter(Boolean)));
  const uniqueContainerHeader = containerList.join(', ');

  // Mutations for SingleDocCard
  const uploadQuote = useUploadQuoteDocument();
  const updateQuote = useUpdateQuoteDocument();
  const deleteQuote = useDeleteQuoteDocument();

  const uploadDraftBl = useUploadDraftBlDocument();
  const updateDraftBl = useUpdateDraftBlDocument();
  const deleteDraftBl = useDeleteDraftBlDocument();

  return (
    <div className="p-3">
      {/* ── Context bar ── */}
      <div className="d-flex align-items-center gap-2 mb-4 flex-wrap">
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={() => navigate('/business/data-management')}
        >
          ← Back
        </button>
        {fileName && (
          <span className="badge rounded-pill text-bg-primary fs-6 fw-normal px-3">
            📁 {fileName}
          </span>
        )}
        {blNo && (
          <span className="badge rounded-pill text-bg-secondary fw-normal px-3">
            BL: {blNo}
          </span>
        )}
        {uniqueContainerHeader && (
          <span className="badge rounded-pill text-bg-dark fw-normal px-3">
            Container: {uniqueContainerHeader}
          </span>
        )}
      </div>

      {/* ── Draft BL Document ── */}
      <SingleDocCard
        title="Draft BL Document"
        icon="📝"
        fileName={fileName}
        blNo={blNo}
        useGetQuery={useDraftBlDocument}
        uploadMutation={uploadDraftBl}
        updateMutation={updateDraftBl}
        deleteMutation={deleteDraftBl}
      />

      {/* ── Proforma / Quote Document ── */}
      <SingleDocCard
        title="Proforma Document (Quote)"
        icon="📃"
        fileName={fileName}
        blNo={blNo}
        useGetQuery={useQuoteDocument}
        uploadMutation={uploadQuote}
        updateMutation={updateQuote}
        deleteMutation={deleteQuote}
      />

      {/* ── Copy Documents ── */}
      <CopyDocsCard fileName={fileName} blNo={blNo} />

      {/* ── Container Images ── */}
      <ContainerImagesCard fileName={fileName} blNo={blNo} containerList={containerList} />
    </div>
  );
};

export default DocumentUploadView;
